import {
  MFAMethod,
  OrganizationMembership,
  OrganizationMembershipRole,
  OrganizationMembershipStatus,
  User,
  UserAuthResponse,
  UserFlag,
} from '@/models/user.models';
import {
  clearOrgDataFromLocalStorage,
  LocalStorageKeys,
} from '@/utils/local-storage';
import { defineStore } from 'pinia';
import { LocalStorage } from 'quasar';
import { axiosInstance } from '@/boot/axios';
import {
  init as initEvents,
  initUserChannel,
  initTeamChannels,
} from '@/store/events';
import { UserModule } from '@/modules/user.module';
import { UserSignUp } from '@/models/auth.models';
import Gleap from 'gleap';
import jwtDecode from 'jwt-decode';
import { useOrganizationStore } from './organizationStore';
import { AwsRum } from 'aws-rum-web';
import { ref, watch } from 'vue';
import { client, TimeEntry } from '@/api';
import { getPusherSocketId } from '@/store/events';

interface WindowWithAwsRum extends Window {
  awsRum?: AwsRum;
}

interface JwtPayload {
  exp: number;
  [key: string]: unknown;
}

const isPusherInitialized = ref(false);

export interface AuthStateInterface {
  user: User | null;
  avatarUploading: boolean;
}

const userModule = new UserModule();

export const useAuthStore = defineStore('auth', {
  /****
   STATE
   *****/
  state: (): AuthStateInterface => {
    return {
      user: null,
      avatarUploading: false,
    };
  },

  /****
   GETTERS
   ****/
  getters: {
    isLoggedIn(): boolean {
      return this.user !== null;
    },
    currentUserMembership(): OrganizationMembership | undefined {
      const organizationStore = useOrganizationStore();
      const org = organizationStore.activeOrganization;
      if (!org || !this.user) {
        return;
      }
      return this.user.organizationMemberships.find(
        (membership: OrganizationMembership) =>
          membership.organizationId === org.id
      );
    },
    isAdmin(): boolean {
      const organizationStore = useOrganizationStore();
      const org = organizationStore.activeOrganization;
      if (org) {
        const membership = this.user?.organizationMemberships.find(
          (m) => m.organizationId === org.id
        );
        return (
          membership?.role === OrganizationMembershipRole.ADMIN ||
          membership?.role === OrganizationMembershipRole.OWNER
        );
      } else return false;
    },
    isOwner(): boolean {
      const organizationStore = useOrganizationStore();
      const org = organizationStore.activeOrganization;
      if (org) {
        const membership = this.user?.organizationMemberships.find(
          (m) => m.organizationId === org.id
        );
        return membership?.role === OrganizationMembershipRole.OWNER;
      } else return false;
    },
    isDevStrideAdmin(): boolean {
      if (this.isAdmin && this.currentUserMembership) {
        return this.currentUserMembership.user.email.includes('@devstride.com');
      }
      return false;
    },
  },

  /****
   ACTIONS
   *****/
  actions: {
    setAvatarUploading(value: boolean) {
      this.avatarUploading = value;
    },

    setUser(data: User | null) {
      if (this.user && data && !data.organizationMemberships?.length) {
        // Sometimes the user response does not contain the organizationMemberships
        // We need to handle this case
        const memberships = [...(this.user.organizationMemberships || [])];
        memberships.forEach((m) => {
          m.user = {
            id: data.id,
            avatarUrl: data.avatarUrl,
            username: data.username,
            name: data.name,
            email: data.email,
            status: data.status,
            timezone: data.timezone,
            mfaMethod: data.mfaMethod,
            passwordLastUpdated: data.passwordLastUpdated,
            phoneNumber: data.phoneNumber || '',
          };
        });
        this.user = { ...data, organizationMemberships: memberships };
        return;
      }
      this.user = data;
    },

    deactivateOrganizationMembershipInCache(orgId: string) {
      if (!this.user) return;
      this.user.organizationMemberships = this.user.organizationMemberships.map(
        (membership: OrganizationMembership) => {
          if (membership.organizationId === orgId) {
            membership.status = OrganizationMembershipStatus.INACTIVE;
          }
          return membership;
        }
      );
    },

    setActiveTimeEntry(entry: TimeEntry | null) {
      // TODO: implement
      entry;
    },

    async storeUser(loginData: UserAuthResponse) {
      if (!(loginData.user && loginData.token)) {
        return;
      }
      LocalStorage.set(
        LocalStorageKeys.ACCESS_TOKEN,
        loginData.token.accessToken
      );
      LocalStorage.set(
        LocalStorageKeys.REFRESH_TOKEN,
        loginData.token.refreshToken
      );
      LocalStorage.set(LocalStorageKeys.ID_TOKEN, loginData.token.idToken);
      if (loginData.user.flags.includes(UserFlag.NEEDS_LOGOUT)) {
        await this.refreshToken();
        return;
      }
      this.setUser(loginData.user);
      const accessToken = getAccessToken();
      if (accessToken) {
        const initialized = initEvents(accessToken);
        isPusherInitialized.value = initialized;
        // if (initialized) {
        //   initUserChannel(loginData.user);
        //   // Initialize team channels for the user's teams
        //   const organizationStore = useOrganizationStore();
        //   const activeOrg = organizationStore.activeOrganization;
        //   if (activeOrg && loginData.user.organizationMemberships) {
        //     const membership = loginData.user.organizationMemberships.find(
        //       (m) => m.organizationId === activeOrg.id
        //     );
        //     if (membership && membership.teams) {
        //       initTeamChannels(membership.teams);
        //     } else {
        //       initTeamChannels([]);
        //     }
        //   }
        // }
      }
      LocalStorage.set(LocalStorageKeys.USER, loginData.user);
      const windowWithRum = window as WindowWithAwsRum;
      if (windowWithRum.awsRum) {
        windowWithRum.awsRum.addSessionAttributes({
          id: loginData.user.id,
          name: loginData.user.name,
          email: loginData.user.email,
        });
      }
    },

    async init() {
      storeAuthentication();
      const user = LocalStorage.getItem<User>(LocalStorageKeys.USER);
      if (user) {
        this.setUser(user);
      }

      // refresh refresh token in the background
      const refreshToken = getRefreshToken();
      if (refreshToken) this.refreshToken();

      if (user) {
        // initialize pusher in the background
        const authStore = useAuthStore();
        authStore.onPusherInitialized(() => {
          initUserChannel(user);
          // Initialize team channels for the user's teams
          const organizationStore = useOrganizationStore();
          const activeOrg = organizationStore.activeOrganization;
          if (activeOrg && user.organizationMemberships) {
            const membership = user.organizationMemberships.find(
              (m) => m.organizationId === activeOrg.id
            );
            if (membership && membership.teams) {
              initTeamChannels(activeOrg.id, membership.teams);
            } else {
              initTeamChannels(activeOrg.id, []);
            }
          }
        });
      }
    },

    async login(loginData: { username: string; password: string }) {
      const loginResponse = await userModule.signIn(loginData);
      if (loginResponse.challenge) {
        return loginResponse;
      }
      await this.storeUser(loginResponse);
      return loginResponse;
    },
    signUp(signUpData: UserSignUp) {
      return userModule.signUp({
        firstName: signUpData.firstName,
        lastName: signUpData.lastName,
        email: signUpData.email,
        avatarUrl: signUpData.avatarUrl,
        password: signUpData.password,
        timezone: signUpData.timezone,
        dateFormat: signUpData.dateFormat,
        title: signUpData.title,
        jobRoles: signUpData.jobRoles,
        createOrganizationName: signUpData.createOrganizationName,
      });
    },

    async setupUserSmsMfa() {
      const user = await userModule.setMyPreferredMfa({
        mfaMethod: MFAMethod.SMS,
      });
      this.setUser(user);
      LocalStorage.set(LocalStorageKeys.USER, user);
      return user;
    },

    async setupUserTotpMfa() {
      const totpResponse = await userModule.setupMyTOTP();
      return totpResponse.code;
    },

    async verifyUserTotpMfaSetup(code: string) {
      const user = await userModule.setMyPreferredMfa({
        mfaMethod: MFAMethod.TOTP,
        code,
      });
      this.setUser(user);
      LocalStorage.set(LocalStorageKeys.USER, user);
      return user;
    },

    async removeUserMFA() {
      const user = await userModule.setMyPreferredMfa({
        mfaMethod: MFAMethod.NO_MFA,
      });
      this.setUser(user);
      LocalStorage.set(LocalStorageKeys.USER, user);
      return user;
    },

    async confirmSignIn(data: {
      username: string;
      session: string;
      code: string;
    }) {
      const loginResponse = await userModule.confirmSignIn(data);
      await this.storeUser(loginResponse);
      return loginResponse;
    },

    confirmSignUp({ username, code }: { username: string; code: string }) {
      return userModule.confirmSignUp({
        username,
        code,
      });
    },

    async refreshToken() {
      const refreshToken = LocalStorage.getItem<string>(
        LocalStorageKeys.REFRESH_TOKEN
      );
      if (refreshToken) {
        try {
          const loginResponse = await userModule.refreshToken({
            refreshToken,
          });
          await this.storeUser(loginResponse);
        } catch (e) {
          console.error(e);
          await this.logout();
        }
      }
    },
    async confirmUserPhoneNumber({ code }: { code: string }) {
      const user = await userModule.verifyMyPhoneNumber({
        code,
      });
      this.setUser(user);
      LocalStorage.set(LocalStorageKeys.USER, user);
      return user;
    },
    logout(location?: string) {
      LocalStorage.remove(LocalStorageKeys.ACCESS_TOKEN);
      LocalStorage.remove(LocalStorageKeys.REFRESH_TOKEN);
      LocalStorage.remove(LocalStorageKeys.ID_TOKEN);
      LocalStorage.remove(LocalStorageKeys.USER);
      clearOrgDataFromLocalStorage();
      if (import.meta.env.VITE_GLEAP_API_KEY) {
        Gleap.clearIdentity();
      }
      if (!location) {
        window.location.reload();
      } else {
        window.location.href = location;
      }
    },
    forgotPassword(username: string) {
      return userModule.sendForgotPasswordCode({
        username,
      });
    },
    forgotPasswordSubmit({
      username,
      code,
      password,
    }: {
      username: string;
      code: string;
      password: string;
    }) {
      return userModule.confirmNewPassword({
        username,
        code,
        newPassword: password,
      });
    },
    async userExists(emailOrUsername: string, ignoreInvited = false) {
      return userModule.checkIfUserExists(emailOrUsername, ignoreInvited);
    },

    async resendVerificationCode(username: string) {
      return userModule.resendVerificationCode(username);
    },

    async onPusherInitialized(cb: () => void) {
      if (isPusherInitialized.value) {
        cb();
      } else {
        const unwatch = watch(isPusherInitialized, (isInitialized) => {
          if (!isInitialized) return;
          cb();
          unwatch();
        });
      }
    },
  },
});

function getAccessToken() {
  const accessToken = LocalStorage.getItem<string>(
    LocalStorageKeys.ACCESS_TOKEN
  );
  if (accessToken) {
    const decodedToken = jwtDecode<JwtPayload>(accessToken);
    // check if token is expired
    if (decodedToken.exp < Date.now() / 1000) {
      return;
    }
    return accessToken;
  }
}

function getRefreshToken() {
  return LocalStorage.getItem<string>(LocalStorageKeys.REFRESH_TOKEN);
}

function storeAuthentication() {
  const authStore = useAuthStore();
  client.interceptors.request.use(async (config) => {
    if (config.url?.includes('refresh-tokens')) {
      return config;
    }

    // Add Pusher socket ID for broadcast exclusion
    const socketId = getPusherSocketId();
    if (socketId) {
      config.headers.set('X-PUNO-SOCKET-ID', socketId);
    }

    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.set('Authorization', accessToken);
      return config;
    }
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await authStore.refreshToken();
      const accessToken = getAccessToken();
      if (accessToken) {
        config.headers.set('Authorization', accessToken);
        return config;
      }
    }
    return config;
  });
  axiosInstance.interceptors.request.use(async (config) => {
    if (config.url?.includes('refresh-tokens')) {
      return config;
    }
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = accessToken;
      return config;
    }
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await authStore.refreshToken();
      const accessToken = getAccessToken();
      if (accessToken) {
        config.headers.Authorization = accessToken;
        return config;
      }
    }
    return config;
  });
}
